import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  Repeat, Loader2, Plus, Ban, Calendar, CheckCircle2, XCircle,
  AlertTriangle, Clock,
} from 'lucide-react';
import PageShell from '../../../components/leaves/PageShell';
import Button from '../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle, DialogDescription } from '../../../components/ui/Dialog';
import { RecurringLeavePatternView, RecurringLeavePreviewView, LeaveType } from '../../../models/attendance';
import * as LeavesApi from '../../../api/Attendance/leaves';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { appDate, appDayMonth } from '../../../utils/appDateFormat';
import { DateField } from '../../../components/dateFields';

interface RecurringLeaveProps {
  userId: string;
  company: { id: number };
  role: string;
}

const statusConfig: Record<string, { bgClass: string; icon: React.ElementType }> = {
  Active: { bgClass: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  Completed: { bgClass: 'bg-blue-100 text-blue-800', icon: CheckCircle2 },
  Cancelled: { bgClass: 'bg-muted text-muted-foreground', icon: Ban },
};

const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatDate(dateStr: string): string {
  return appDate(new Date(dateStr));
}

function formatShortDate(dateStr: string): string {
  return appDayMonth(new Date(dateStr));
}

const RecurringLeave: React.FC<RecurringLeaveProps> = () => {
  const { t } = useTranslation(['leaves', 'common']);

  const [patterns, setPatterns] = useState<RecurringLeavePatternView[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [createModal, setCreateModal] = useState(false);
  const [cancelModal, setCancelModal] = useState<RecurringLeavePatternView | null>(null);
  const [preview, setPreview] = useState<RecurringLeavePreviewView | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const [form, setForm] = useState({
    leaveTypeId: 0,
    recurrenceType: 0, // 0=Weekly, 1=BiWeekly, 2=Monthly
    dayOfWeek: 3, // Wednesday
    dayOfMonth: 1,
    startDate: '',
    endDate: '',
    comments: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [patternsRes, typesRes] = await Promise.all([
        LeavesApi.getMyRecurringPatterns(),
        LeavesApi.getCompanyLeaveTypes(),
      ]);
      if (patternsRes.errors.length === 0) setPatterns(patternsRes.payload);
      if (typesRes.errors.length === 0) setLeaveTypes(typesRes.payload);
    } catch {
      setToast({ message: t('recurringLoadError'), type: 'error' });
    }
    setLoading(false);
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handlePreview = async () => {
    if (!form.leaveTypeId || !form.startDate || !form.endDate) return;
    setPreviewLoading(true);
    setPreview(null);
    try {
      const input = {
        leaveTypeId: form.leaveTypeId,
        recurrenceType: form.recurrenceType,
        dayOfWeek: form.recurrenceType <= 1 ? form.dayOfWeek : undefined,
        dayOfMonth: form.recurrenceType === 2 ? form.dayOfMonth : undefined,
        startDate: form.startDate,
        endDate: form.endDate,
        comments: form.comments || undefined,
      };
      const result = await LeavesApi.previewRecurringLeave(input);
      if (result.errors.length === 0) {
        setPreview(result.payload);
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('recurringPreviewError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('recurringPreviewError')), type: 'error' });
    }
    setPreviewLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.leaveTypeId || !form.startDate || !form.endDate) return;
    setSubmitLoading(true);
    try {
      const input = {
        leaveTypeId: form.leaveTypeId,
        recurrenceType: form.recurrenceType,
        dayOfWeek: form.recurrenceType <= 1 ? form.dayOfWeek : undefined,
        dayOfMonth: form.recurrenceType === 2 ? form.dayOfMonth : undefined,
        startDate: form.startDate,
        endDate: form.endDate,
        comments: form.comments || undefined,
      };
      const result = await LeavesApi.submitRecurringLeave(input);
      if (result.errors.length === 0) {
        setToast({ message: t('recurringSubmitted'), type: 'success' });
        setCreateModal(false);
        setPreview(null);
        setForm({ leaveTypeId: 0, recurrenceType: 0, dayOfWeek: 3, dayOfMonth: 1, startDate: '', endDate: '', comments: '' });
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('recurringSubmitError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('recurringSubmitError')), type: 'error' });
    }
    setSubmitLoading(false);
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    setActionLoading(cancelModal.id);
    try {
      const result = await LeavesApi.cancelRecurringPattern(cancelModal.id);
      if (result.errors.length === 0) {
        setToast({ message: t('recurringCancelled'), type: 'success' });
        setCancelModal(null);
        fetchData();
      } else {
        setToast({ message: getApiErrorMessage(result, t, t('recurringCancelError')), type: 'error' });
      }
    } catch (err) {
      setToast({ message: getApiErrorMessage(err, t, t('recurringCancelError')), type: 'error' });
    }
    setActionLoading(null);
  };

  const recurrenceLabels = [t('Weekly'), t('Bi-Weekly'), t('Monthly')];

  const stats = useMemo(() => {
    const active = patterns.filter(p => p.status === 'Active').length;
    const totalOccurrences = patterns.reduce((sum, p) => sum + p.totalOccurrences, 0);
    const totalDays = patterns.reduce((sum, p) => sum + p.totalDaysDeducted, 0);
    return [
      { label: t('Active Patterns'), value: active, color: 'text-green-600' },
      { label: t('Occurrences'), value: totalOccurrences },
      { label: t('Total Days'), value: totalDays.toFixed(1) },
    ];
  }, [patterns, t]);

  const renderStatusBadge = (status: string) => {
    const config = statusConfig[status] || statusConfig.Active;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgClass}`}>
        <Icon className="w-3 h-3" />
        {t(status)}
      </span>
    );
  };

  const renderPatternDescription = (pattern: RecurringLeavePatternView) => {
    if (pattern.recurrenceType === 'Weekly' || pattern.recurrenceType === 'BiWeekly') {
      const prefix = pattern.recurrenceType === 'BiWeekly' ? t('Every other') : t('Every');
      return `${prefix} ${t(pattern.dayOfWeek || '')}`;
    }
    if (pattern.recurrenceType === 'Monthly') {
      return t('monthlyOnDay', { day: pattern.dayOfMonth });
    }
    return pattern.recurrenceType;
  };

  return (
    <PageShell
      title={t('Recurring Leave')}
      description={t('recurringLeaveDescription')}
      icon={Repeat}
      accentColor="emerald"
      maxWidth="5xl"
      stats={stats}
      headerActions={(
        <Button
          variant="outline"
          onClick={() => {
            setPreview(null);
            if (leaveTypes.length > 0) setForm(f => ({ ...f, leaveTypeId: leaveTypes[0].id }));
            setCreateModal(true);
          }}
          data-testid="create-recurring-btn"
          iconName="Plus"
        >
          {t('Create Pattern')}
        </Button>
      )}
      className="min-h-screen bg-background"
    >

      {/* Pattern list */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
        </div>
      ) : patterns.length === 0 ? (
        <div className="text-center py-20" data-testid="recurring-empty">
          <Repeat className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground font-medium">{t('noRecurringPatterns')}</p>
          <p className="text-sm text-muted-foreground/70 mt-1">{t('noRecurringPatternsDescription')}</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {patterns.map(pattern => (
            <div key={pattern.id} className="bg-background rounded-xl border border-border p-5 hover:shadow-md transition-shadow" data-testid={`pattern-card-${pattern.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <Repeat className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{pattern.leaveTypeName}</p>
                    <p className="text-xs text-muted-foreground">{renderPatternDescription(pattern)}</p>
                  </div>
                </div>
                {renderStatusBadge(pattern.status)}
              </div>

              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">{t('Date Range')}</p>
                  <p className="text-sm font-medium text-foreground">
                    {formatDate(pattern.startDate)} - {formatDate(pattern.endDate)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('Occurrences')}</p>
                  <p className="text-sm font-medium text-foreground">{pattern.totalOccurrences}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('Total Days')}</p>
                  <p className="text-sm font-medium text-foreground">{pattern.totalDaysDeducted.toFixed(1)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{t('Created')}</p>
                  <p className="text-sm font-medium text-foreground">{formatDate(pattern.createdOn)}</p>
                </div>
              </div>

              {pattern.comments && (
                <div className="mt-3">
                  <p className="text-xs text-muted-foreground">{t('Comments')}</p>
                  <p className="text-sm text-foreground/80 mt-0.5">{pattern.comments}</p>
                </div>
              )}

              {/* Occurrence dates */}
              {pattern.occurrences.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-muted-foreground mb-1.5">{t('Scheduled Dates')}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {pattern.occurrences.slice(0, 12).map((date, i) => (
                      <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">
                        <Calendar className="w-3 h-3" />
                        {formatShortDate(date)}
                      </span>
                    ))}
                    {pattern.occurrences.length > 12 && (
                      <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded text-xs">
                        +{pattern.occurrences.length - 12} {t('more')}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {pattern.status === 'Active' && (
                <div className="mt-4">
                  <button
                    onClick={() => setCancelModal(pattern)}
                    disabled={actionLoading === pattern.id}
                    className="flex items-center gap-1.5 px-3 py-2 bg-muted text-foreground/80 rounded-lg text-sm font-medium hover:bg-muted/80 disabled:opacity-50 transition-colors"
                    data-testid={`cancel-pattern-${pattern.id}`}
                  >
                    {actionLoading === pattern.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                    {t('Cancel Pattern')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Dialog open={createModal} onOpenChange={(o) => { if (!o) { setCreateModal(false); setPreview(null); } }}>
        <DialogContent size="lg" data-testid="create-recurring-modal">
          <DialogHeader>
            <DialogTitle>{t('Create Recurring Leave')}</DialogTitle>
            <DialogDescription>{t('recurringCreateDescription')}</DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            {/* Leave Type */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">{t('Leave Type')}</label>
              <select
                value={form.leaveTypeId}
                onChange={(e) => setForm({ ...form, leaveTypeId: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                data-testid="recurring-leave-type"
              >
                <option value={0}>{t('Select leave type')}</option>
                {leaveTypes.map(lt => (
                  <option key={lt.id} value={lt.id}>{lt.name}</option>
                ))}
              </select>
            </div>

            {/* Recurrence Type */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">{t('Recurrence')}</label>
              <div className="flex gap-2">
                {recurrenceLabels.map((label, i) => (
                  <button
                    key={i}
                    onClick={() => setForm({ ...form, recurrenceType: i })}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      form.recurrenceType === i
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                    data-testid={`recurrence-${i}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Day selector */}
            {form.recurrenceType <= 1 ? (
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1">{t('Day of Week')}</label>
                <select
                  value={form.dayOfWeek}
                  onChange={(e) => setForm({ ...form, dayOfWeek: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="recurring-day-of-week"
                >
                  {daysOfWeek.map((day, i) => (
                    <option key={i} value={i}>{t(day)}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1">{t('Day of Month')}</label>
                <input
                  type="number"
                  min={1}
                  max={28}
                  value={form.dayOfMonth}
                  onChange={(e) => setForm({ ...form, dayOfMonth: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                  data-testid="recurring-day-of-month"
                />
              </div>
            )}

            {/* Date range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1">{t('Start Date')}</label>
                <DateField
                  value={form.startDate}
                  onChange={(v) => setForm({ ...form, startDate: v })}
                  data-testid="recurring-start-date"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground/80 mb-1">{t('End Date')}</label>
                <DateField
                  value={form.endDate}
                  onChange={(v) => setForm({ ...form, endDate: v })}
                  data-testid="recurring-end-date"
                />
              </div>
            </div>

            {/* Comments */}
            <div>
              <label className="block text-sm font-medium text-foreground/80 mb-1">{t('Comments')}</label>
              <textarea
                value={form.comments}
                onChange={(e) => setForm({ ...form, comments: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                placeholder={t('recurringCommentsPlaceholder')}
                data-testid="recurring-comments"
              />
            </div>

            {/* Preview button */}
            <Button
              onClick={handlePreview}
              disabled={previewLoading || !form.leaveTypeId || !form.startDate || !form.endDate}
              variant="secondary"
              fullWidth
              loading={previewLoading}
              iconName="Calendar"
              data-testid="preview-recurring-btn"
            >
              {t('Preview Occurrences')}
            </Button>

            {/* Preview result */}
            {preview && (
              <div className="bg-primary/10 rounded-xl p-4 space-y-3" data-testid="recurring-preview">
                <h4 className="text-sm font-semibold text-foreground">{t('Preview')}</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-primary">{t('Occurrences')}:</span>
                    <span className="font-semibold text-foreground ms-1">{preview.totalOccurrences}</span>
                  </div>
                  <div>
                    <span className="text-primary">{t('Total Days')}:</span>
                    <span className="font-semibold text-foreground ms-1">{preview.totalDaysDeducted.toFixed(1)}</span>
                  </div>
                  <div>
                    <span className="text-primary">{t('Current Balance')}:</span>
                    <span className="font-semibold text-foreground ms-1">{preview.currentBalance.toFixed(1)}</span>
                  </div>
                  <div>
                    <span className="text-primary">{t('Balance After')}:</span>
                    <span className={`font-semibold ms-1 ${preview.balanceAfter < 0 ? 'text-red-600' : 'text-foreground'}`}>
                      {preview.balanceAfter.toFixed(1)}
                    </span>
                  </div>
                </div>
                {preview.balanceAfter < 0 && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    {t('insufficientBalanceWarning')}
                  </p>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {preview.occurrences.map((date, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-background text-primary rounded text-xs border border-primary/20">
                      <Calendar className="w-3 h-3" />
                      {formatShortDate(date)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => { setCreateModal(false); setPreview(null); }} variant="outline" data-testid="cancel-create-btn">
              {t('Cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitLoading || !preview || preview.balanceAfter < 0}
              loading={submitLoading}
              data-testid="submit-recurring-btn"
            >
              {t('Submit Recurring Leave')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Modal */}
      <Dialog open={!!cancelModal} onOpenChange={(o) => { if (!o) setCancelModal(null); }}>
        <DialogContent size="sm" data-testid="cancel-recurring-modal">
          <DialogBody className="text-center pt-6">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-foreground">{t('Cancel Recurring Pattern')}</h3>
            <p className="text-sm text-muted-foreground mt-2">
              {cancelModal && t('confirmCancelRecurring', { type: cancelModal.leaveTypeName, count: cancelModal.totalOccurrences })}
            </p>
            <p className="text-xs text-muted-foreground/70 mt-2">{t('cancelRecurringNote')}</p>
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => setCancelModal(null)} variant="outline" className="flex-1">
              {t('Keep')}
            </Button>
            <Button
              onClick={handleCancel}
              disabled={cancelModal ? actionLoading === cancelModal.id : false}
              variant="destructive"
              className="flex-1"
              loading={cancelModal ? actionLoading === cancelModal.id : false}
              data-testid="confirm-cancel-recurring-btn"
            >
              {t('Cancel Pattern')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 end-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-success text-success-foreground' : 'bg-destructive text-destructive-foreground'
        }`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {toast.message}
        </div>
      )}
    </PageShell>
  );
};

const mapStateToProps = (state: { user: { userId: string; company: { id: number }; role: string } }) => ({
  userId: state.user.userId,
  company: state.user.company,
  role: state.user.role,
});

export default connect(mapStateToProps)(RecurringLeave);
